import {
  VEHICLE_SPECS,
  type RankingEntry,
  type RoomView,
  type ServerMessage,
  type SessionResponse,
  type VehicleType,
} from "@racemax/shared";
import { GameAudio } from "./game/Audio";
import type { RacemaxGame, RaceTelemetry } from "./game/Game";
import { createRoom, joinRoom, RoomClient, type ConnectionState } from "./net/RoomClient";

const SESSION_KEY = "racemax.session.v1";
const COLOR_CSS = { yellow: "#f5dd35", green: "#55db72", red: "#f25540", blue: "#42a5f5" } as const;

function element<T extends HTMLElement>(id: string): T {
  const result = document.getElementById(id);
  if (!result) throw new Error(`Élément #${id} introuvable`);
  return result as T;
}

function formatTime(milliseconds: number | null): string {
  if (milliseconds === null) return "DNF";
  const safe = Math.max(0, milliseconds);
  const minutes = Math.floor(safe / 60_000);
  const seconds = Math.floor((safe % 60_000) / 1_000);
  const millis = Math.floor(safe % 1_000);
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}.${String(millis).padStart(3, "0")}`;
}

export class RacemaxApp {
  private readonly home = element<HTMLElement>("home-screen");
  private readonly lobby = element<HTMLElement>("lobby-screen");
  private readonly raceHud = element<HTMLElement>("race-hud");
  private readonly finish = element<HTMLElement>("finish-screen");
  private readonly loading = element<HTMLElement>("loading-overlay");
  private readonly nameInput = element<HTMLInputElement>("player-name");
  private readonly codeInput = element<HTMLInputElement>("room-code");
  private readonly createButton = element<HTMLButtonElement>("create-room");
  private readonly joinButton = element<HTMLButtonElement>("join-room");
  private readonly startButton = element<HTMLButtonElement>("start-race");
  private readonly formError = element<HTMLElement>("form-error");
  private readonly toastElement = element<HTMLElement>("toast");
  private readonly audio = new GameAudio();

  private session: SessionResponse | null = null;
  private room: RoomView | null = null;
  private client: RoomClient | null = null;
  private game: RacemaxGame | null = null;
  private gameLoading: Promise<void> | null = null;
  private pendingGameMessages: ServerMessage[] = [];
  private toastTimer = 0;
  private muted = false;

  constructor() {
    this.bindUi();
    this.restoreName();
    const saved = this.restoreSession();
    if (saved) {
      this.loading.classList.remove("hidden");
      this.beginSession(saved);
    }
  }

  private bindUi(): void {
    this.createButton.addEventListener("click", () => void this.create());
    this.joinButton.addEventListener("click", () => void this.join());
    this.startButton.addEventListener("click", () => this.client?.send({ type: "start_race" }));
    element<HTMLButtonElement>("copy-code").addEventListener("click", () => void this.copyCode());
    element<HTMLButtonElement>("new-race").addEventListener("click", () => this.newRace());
    element<HTMLButtonElement>("leave-lobby").addEventListener("click", () => this.newRace());
    element<HTMLButtonElement>("sound-toggle").addEventListener("click", () => this.toggleSound());
    this.codeInput.addEventListener("input", () => { this.codeInput.value = this.codeInput.value.toUpperCase().replace(/[^A-HJ-NP-Z2-9]/g, "").slice(0, 6); });
    this.nameInput.addEventListener("keydown", (event) => { if (event.key === "Enter") void this.create(); });
    this.codeInput.addEventListener("keydown", (event) => { if (event.key === "Enter") void this.join(); });
    for (const card of document.querySelectorAll<HTMLButtonElement>(".vehicle-card")) {
      card.addEventListener("click", () => {
        const vehicle = card.dataset.vehicle as VehicleType | undefined;
        if (vehicle) this.client?.send({ type: "select_vehicle", vehicle });
      });
    }
    window.addEventListener("beforeunload", () => this.client?.close());
  }

  private async create(): Promise<void> {
    const name = this.validName();
    if (!name) return;
    void this.audio.unlock();
    await this.runSessionRequest(() => createRoom(name));
  }

  private async join(): Promise<void> {
    const name = this.validName();
    if (!name) return;
    const code = this.codeInput.value.trim().toUpperCase();
    if (!/^[A-HJ-NP-Z2-9]{6}$/.test(code)) {
      this.formError.textContent = "Saisis un code de salon à 6 caractères.";
      return;
    }
    void this.audio.unlock();
    await this.runSessionRequest(() => joinRoom(code, name));
  }

  private async runSessionRequest(request: () => Promise<SessionResponse>): Promise<void> {
    this.formError.textContent = "";
    this.createButton.disabled = true;
    this.joinButton.disabled = true;
    try {
      const session = await request();
      localStorage.setItem("racemax.name", this.nameInput.value.trim());
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
      this.beginSession(session);
    } catch (error) {
      this.formError.textContent = error instanceof Error ? error.message : "Impossible de rejoindre la course.";
    } finally {
      this.createButton.disabled = false;
      this.joinButton.disabled = false;
    }
  }

  private beginSession(session: SessionResponse): void {
    this.session = session;
    this.client?.close();
    const client = new RoomClient(session);
    this.client = client;
    client.onMessage = (message) => this.handleMessage(message);
    client.onConnectionState = (state) => this.handleConnection(state);
    client.connect();
  }

  private handleMessage(message: ServerMessage): void {
    if (message.type === "error") {
      this.toast(message.message);
      return;
    }
    if (message.type === "welcome" || message.type === "room_state") {
      this.room = message.room;
      this.loading.classList.add("hidden");
      if (message.room.status === "lobby") this.showLobby();
      this.renderRoom();
      if ((message.room.status === "countdown" || message.room.status === "racing") && message.room.startedAt !== null) {
        void this.startGame(message.room.startedAt);
      }
      if (message.room.status === "finished") this.renderFinalRanking(message.room.ranking, true);
    } else if (message.type === "race_start") {
      void this.startGame(message.startsAt);
    } else if (message.type === "race_cancelled") {
      this.toast(message.reason);
      this.stopGameToLobby();
    } else if (message.type === "lap_update") {
      const player = this.room?.players.find((candidate) => candidate.id === message.playerId);
      if (player) {
        player.lap = message.lap;
        player.nextCheckpoint = message.nextCheckpoint;
      }
      if (message.playerId === this.session?.playerId) element("lap-current").textContent = String(Math.min(3, message.lap + 1));
    } else if (message.type === "ranking_update") {
      if (this.room) this.room.ranking = message.ranking;
      this.renderRaceRankings(message.ranking);
      if (message.complete) this.renderFinalRanking(message.ranking, true);
    }

    if (message.type === "player_snapshot" || message.type === "respawn" || message.type === "race_start") {
      if (this.game) this.game.handleMessage(message);
      else this.pendingGameMessages.push(message);
    }
  }

  private handleConnection(state: ConnectionState): void {
    const pill = element("connection-pill");
    pill.classList.toggle("reconnecting", state !== "connected");
    pill.innerHTML = `<span></span>${state === "connected" ? " CONNECTÉ" : state === "closed" ? " DÉCONNECTÉ" : " RECONNEXION…"}`;
    if (state === "closed" && this.session) this.toast("Connexion perdue. La place a été réservée pendant 30 secondes.");
  }

  private async startGame(startsAt: number): Promise<void> {
    if (!this.room || !this.session || !this.client) return;
    if (this.game) {
      this.game.setStartTime(startsAt);
      return;
    }
    if (this.gameLoading) return this.gameLoading;
    this.gameLoading = (async () => {
      this.loading.classList.remove("hidden");
      this.home.classList.add("hidden");
      this.lobby.classList.add("hidden");
      const { RacemaxGame: Game } = await import("./game/Game");
      const game = new Game(element<HTMLCanvasElement>("game-canvas"), this.client!, this.audio, {
        onTelemetry: (telemetry) => this.renderTelemetry(telemetry),
        onCountdownCue: (label) => this.audio.countdown(label),
        onRespawnRequested: () => this.toast("Retour au dernier checkpoint…"),
      });
      this.game = game;
      try {
        await game.initialize(this.room!, this.session!.playerId, startsAt);
        document.body.classList.add("racing");
        this.raceHud.classList.remove("hidden");
        this.loading.classList.add("hidden");
        element("lap-current").textContent = "1";
        for (const pending of this.pendingGameMessages.splice(0)) game.handleMessage(pending);
      } catch (error) {
        this.loading.classList.add("hidden");
        this.toast(error instanceof Error ? error.message : "Impossible de charger le circuit 3D.");
        this.stopGameToLobby();
      }
    })();
    await this.gameLoading;
    this.gameLoading = null;
  }

  private renderTelemetry(telemetry: RaceTelemetry): void {
    element("speed-value").textContent = String(telemetry.speedKmh);
    element("race-time").textContent = formatTime(telemetry.elapsedMs);
    element("boost-indicator").parentElement?.classList.toggle("boosting", telemetry.boosting);
    const countdown = element("countdown");
    const label = countdown.querySelector("span");
    if (telemetry.countdown && label) {
      countdown.classList.remove("hidden");
      if (label.textContent !== telemetry.countdown) {
        label.textContent = telemetry.countdown === "GO" ? "GOOO!" : telemetry.countdown;
        label.style.animation = "none";
        void label.offsetWidth;
        label.style.animation = "";
      }
    } else {
      countdown.classList.add("hidden");
    }
  }

  private showLobby(): void {
    this.home.classList.add("hidden");
    this.finish.classList.add("hidden");
    this.lobby.classList.remove("hidden");
  }

  private renderRoom(): void {
    if (!this.room || !this.session) return;
    element("lobby-code").textContent = this.room.code;
    element("player-count").textContent = `${this.room.players.length} / 4`;
    const playersList = element("players-list");
    playersList.replaceChildren(...this.room.players.map((player) => {
      const row = document.createElement("div");
      row.className = `player-row${player.connected ? "" : " offline"}`;
      row.style.setProperty("--player-color", COLOR_CSS[player.color]);
      const color = document.createElement("i");
      color.className = "player-color";
      const identity = document.createElement("div");
      const name = document.createElement("strong");
      name.textContent = player.name;
      const vehicle = document.createElement("small");
      vehicle.textContent = player.vehicle ? VEHICLE_SPECS[player.vehicle].label : "Choisit son véhicule…";
      identity.appendChild(name);
      identity.appendChild(document.createElement("br"));
      identity.appendChild(vehicle);
      const role = document.createElement("em");
      role.textContent = player.id === this.room?.hostId ? "HÔTE" : player.connected ? "PRÊT" : "HORS LIGNE";
      row.appendChild(color);
      row.appendChild(identity);
      row.appendChild(role);
      return row;
    }));

    const self = this.room.players.find((player) => player.id === this.session?.playerId);
    const usedVehicles = new Map(this.room.players.filter((player) => player.vehicle).map((player) => [player.vehicle!, player.id]));
    for (const card of document.querySelectorAll<HTMLButtonElement>(".vehicle-card")) {
      const vehicle = card.dataset.vehicle as VehicleType;
      card.classList.toggle("selected", self?.vehicle === vehicle);
      const owner = usedVehicles.get(vehicle);
      card.disabled = this.room.status !== "lobby" || Boolean(owner && owner !== self?.id);
    }

    const isHost = this.room.hostId === this.session.playerId;
    const ready = this.room.players.length >= 2 && this.room.players.every((player) => player.connected && player.vehicle !== null);
    this.startButton.disabled = !isHost || !ready || this.room.status !== "lobby";
    this.startButton.querySelector("span")!.textContent = isHost ? "LANCER LA COURSE" : "EN ATTENTE DE L'HÔTE";
    element("lobby-hint").textContent = this.room.players.length < 2
      ? "Il faut au moins 2 pilotes pour partir."
      : this.room.players.some((player) => !player.vehicle)
        ? "Chaque pilote doit choisir une machine."
        : isHost ? "Tout le monde est prêt. À toi de jouer !" : "Le départ appartient à l'hôte.";
  }

  private renderRaceRankings(ranking: RankingEntry[]): void {
    const container = element("race-rankings");
    container.replaceChildren(...ranking.map((entry) => {
      const row = document.createElement("div");
      row.className = "mini-rank";
      row.style.setProperty("--player-color", COLOR_CSS[entry.color]);
      const rank = document.createElement("b");
      rank.textContent = entry.rank ? `${entry.rank}.` : "—";
      const name = document.createElement("span");
      name.textContent = entry.name;
      const time = document.createElement("time");
      time.textContent = entry.dnf ? "DNF" : formatTime(entry.timeMs);
      row.appendChild(rank);
      row.appendChild(name);
      row.appendChild(time);
      return row;
    }));
  }

  private renderFinalRanking(ranking: RankingEntry[], complete: boolean): void {
    const container = element("final-ranking");
    container.replaceChildren(...ranking.map((entry) => {
      const row = document.createElement("div");
      row.className = "rank-row";
      row.style.setProperty("--player-color", COLOR_CSS[entry.color]);
      const rank = document.createElement("b");
      rank.textContent = entry.rank ? `#${entry.rank}` : "—";
      const dot = document.createElement("i");
      const name = document.createElement("strong");
      name.textContent = entry.name;
      const time = document.createElement("span");
      time.textContent = entry.dnf ? "ABANDON" : formatTime(entry.timeMs);
      row.appendChild(rank);
      row.appendChild(dot);
      row.appendChild(name);
      row.appendChild(time);
      return row;
    }));
    if (complete) {
      this.finish.classList.remove("hidden");
      this.raceHud.classList.add("hidden");
    }
  }

  private stopGameToLobby(): void {
    this.game?.dispose();
    this.game = null;
    this.gameLoading = null;
    this.pendingGameMessages = [];
    this.audio.silenceEngine();
    document.body.classList.remove("racing");
    this.raceHud.classList.add("hidden");
    this.finish.classList.add("hidden");
    this.showLobby();
  }

  private newRace(): void {
    const currentClient = this.client;
    this.session = null;
    currentClient?.close();
    this.client = null;
    this.game?.dispose();
    this.game = null;
    this.gameLoading = null;
    this.room = null;
    this.pendingGameMessages = [];
    this.audio.silenceEngine();
    sessionStorage.removeItem(SESSION_KEY);
    document.body.classList.remove("racing");
    this.finish.classList.add("hidden");
    this.raceHud.classList.add("hidden");
    this.lobby.classList.add("hidden");
    this.loading.classList.add("hidden");
    this.home.classList.remove("hidden");
    this.codeInput.value = "";
  }

  private async copyCode(): Promise<void> {
    if (!this.room) return;
    try {
      await navigator.clipboard.writeText(this.room.code);
      this.toast("Code copié !");
    } catch {
      this.toast(`Code du salon : ${this.room.code}`);
    }
  }

  private toggleSound(): void {
    this.muted = !this.muted;
    this.audio.setMuted(this.muted);
    const button = element<HTMLButtonElement>("sound-toggle");
    button.textContent = this.muted ? "×" : "♪";
    button.setAttribute("aria-label", this.muted ? "Activer le son" : "Couper le son");
  }

  private validName(): string | null {
    const name = this.nameInput.value.normalize("NFKC").replace(/\s+/g, " ").trim();
    if (name.length < 2 || name.length > 16) {
      this.formError.textContent = "Ton nom doit contenir entre 2 et 16 caractères.";
      return null;
    }
    this.formError.textContent = "";
    return name;
  }

  private toast(message: string): void {
    window.clearTimeout(this.toastTimer);
    this.toastElement.textContent = message;
    this.toastElement.classList.remove("hidden");
    this.toastTimer = window.setTimeout(() => this.toastElement.classList.add("hidden"), 3_300);
  }

  private restoreName(): void {
    this.nameInput.value = localStorage.getItem("racemax.name") ?? "";
  }

  private restoreSession(): SessionResponse | null {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    try {
      const session = JSON.parse(raw) as Partial<SessionResponse>;
      if (typeof session.roomCode === "string" && typeof session.playerId === "string" && typeof session.resumeToken === "string") {
        return session as SessionResponse;
      }
    } catch {
      sessionStorage.removeItem(SESSION_KEY);
    }
    return null;
  }
}
